import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { SUB_STATUS_CLS, SUB_STATUS_TR, AUDIT_TR, fmtD, fmtDT } from "@/lib/subscription";

const ActionRow = ({ label, children }) => (
  <div className="flex flex-wrap items-center gap-2 rounded-xl bg-muted/40 p-2"><span className="text-sm font-medium w-full sm:w-40 shrink-0">{label}</span>{children}</div>
);

const DaysAction = ({ action, label, btn, run, testid }) => {
  const [v, setV] = useState("");
  return (
    <ActionRow label={label}>
      <Input type="number" min={1} max={3650} placeholder="gün" className="w-24 h-8" value={v} onChange={(e) => setV(e.target.value)} data-testid={`${testid}-days`} />
      <Button size="sm" disabled={!v} onClick={() => run({ action, days: Number(v) })} data-testid={testid}>{btn}</Button>
    </ActionRow>
  );
};

export const ProviderSubDialog = ({ detail, onClose, onChanged }) => (detail ? <SubDialogBody key={detail.provider.id} detail={detail} onClose={onClose} onChanged={onChanged} /> : null);

const SubDialogBody = ({ detail, onClose, onChanged }) => {
  const [date, setDate] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [reason, setReason] = useState("");
  const { provider: p, subscription: s, history } = detail;
  const run = async (body, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try { await api.post(`/admin/subscriptions/providers/${p.id}/action`, body); toast.success("İşlem kaydedildi"); setDate(""); setPaidDate(""); setReason(""); onChanged(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sub-provider-dialog">
        <DialogHeader><DialogTitle>{p.name} {p.surname || ""} · {p.email}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge className={SUB_STATUS_CLS[s.status]} data-testid="sub-dialog-status">{s.status_label}</Badge>
          <span>Deneme: {fmtD(s.trial_started_at)} – {fmtD(s.trial_ends_at)}</span>
          <span>· Ücretli bitiş: {fmtD(s.paid_until)}</span>
          <span>· Kalan: {s.days_left != null ? `${s.days_left} gün` : "—"}</span>
          {s.suspended_reason && <span className="text-destructive">· Askı nedeni: {s.suspended_reason}</span>}
          {s.trial_review === "pending" && <Badge variant="outline">Deneme incelemesi bekliyor</Badge>}
        </div>
        <div className="space-y-2">
          <DaysAction action="extend_trial" label="Deneme uzat" btn="Uzat" run={run} testid="sub-act-extend" />
          <ActionRow label="Deneme bitiş tarihi">
            <Input type="date" className="w-40 h-8" value={date} onChange={(e) => setDate(e.target.value)} data-testid="sub-act-trial-date" />
            <Button size="sm" disabled={!date} onClick={() => run({ action: "set_trial_end", date })} data-testid="sub-act-set-trial-end">Kaydet</Button>
          </ActionRow>
          <DaysAction action="grant_paid" label="Ücretli süre tanımla" btn="Tanımla" run={run} testid="sub-act-grant" />
          <ActionRow label="Ücretli abonelik bitiş tarihini değiştir">
            <Input type="date" className="w-40 h-8" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} data-testid="sub-act-paid-date" />
            <Button size="sm" disabled={!paidDate} data-testid="sub-act-set-paid-until"
              onClick={() => run({ action: "set_paid_until", date: paidDate }, `Ücretli abonelik bitişi ${fmtD(s.paid_until)} → ${new Date(`${paidDate}T12:00:00`).toLocaleDateString("tr-TR")} olarak değiştirilsin mi?${new Date(`${paidDate}T23:59:59`) < new Date() ? " Geçmiş tarih: başka geçerli hak yoksa işletme süresi dolmuş olur." : ""}`)}>Kaydet</Button>
            <span className="text-[11px] text-muted-foreground w-full">Deneme süresini etkilemez. Geçmiş veya gelecek tarih seçilebilir; durum hemen yeniden hesaplanır.</span>
          </ActionRow>
          <DaysAction action="start_trial" label="Manuel deneme başlat" btn="Başlat" run={run} testid="sub-act-start-trial" />
          <ActionRow label="Süresiz muafiyet">
            {s.exempt ? <Button size="sm" variant="outline" onClick={() => run({ action: "unset_exempt" }, "Muafiyet kaldırılsın mı?")} data-testid="sub-act-unexempt">Muafiyeti kaldır</Button>
              : <Button size="sm" onClick={() => run({ action: "set_exempt" }, "Süresiz ücretsiz kullanım tanımlansın mı?")} data-testid="sub-act-exempt">Süresiz ücretsiz yap</Button>}
          </ActionRow>
          <ActionRow label="Askıya alma">
            {s.suspended ? <Button size="sm" variant="outline" onClick={() => run({ action: "unsuspend" })} data-testid="sub-act-unsuspend">Askıyı kaldır</Button> : <>
              <Input placeholder="Neden (zorunlu)" className="flex-1 min-w-[160px] h-8" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} data-testid="sub-act-suspend-reason" />
              <Button size="sm" variant="destructive" disabled={reason.trim().length < 3} onClick={() => run({ action: "suspend", reason }, "İşletme askıya alınsın mı?")} data-testid="sub-act-suspend">Askıya al</Button></>}
          </ActionRow>
          {s.migration_pending && <ActionRow label="Geçişi sonlandır">
            <span className="text-xs text-muted-foreground flex-1">Kısıtlamasız geçiş durumu kalkar; deneme/ücretli süresi yoksa süresi dolmuş olur.</span>
            <Button size="sm" variant="outline" onClick={() => run({ action: "end_migration_expire" }, "Geçiş durumu sonlandırılsın mı? İşletme yeni işlem yapamayabilir.")} data-testid="sub-act-end-migration">Sonlandır</Button>
          </ActionRow>}
        </div>
        <div>
          <div className="font-semibold text-sm mb-1">İşlem geçmişi</div>
          <div className="max-h-56 overflow-y-auto text-xs space-y-1" data-testid="sub-dialog-history">
            {!history.length && <div className="text-muted-foreground">Kayıt yok.</div>}
            {history.map((h) => (
              <div key={h.id} className="border-b py-1">{fmtDT(h.at)} · <b>{AUDIT_TR[h.action] || h.action}</b> · {h.actor_email}
                {h.meta?.from && ` · ${SUB_STATUS_TR[h.meta.from]} → ${SUB_STATUS_TR[h.meta.to]}`}{h.meta?.days ? ` · ${h.meta.days} gün` : ""}{h.meta?.reason ? ` · ${h.meta.reason}` : ""}{h.meta?.new_paid_until ? ` · Ücretli bitiş: ${fmtD(h.meta.old_paid_until)} → ${fmtD(h.meta.new_paid_until)}` : ""}</div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
